// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 3 miest
// PartneriView — Matrix v2 master-detail layout (po feedbacku Mirka 2026-05-20)
// LAYOUT: vľavo úzky zoznam (380px), vpravo široký detail vybraného partnera
// Šípkou ↑↓ v ľavom zozname → URL sa zmení → pravý panel update LIVE.
// URL: /partneri (no selection), /partneri/:id (id preselected)
//
// ŠTÁTNICA — Ústava 12.0 bod 3 (KRITICKÉ):
// "Žiadna tichá automatika — VŽDY SÚHRN + POTVRDENIE PRED ZÁPISOM DO DB."
// NewPartnerModal teraz má 2 fázy:
//   1. Formulár (editácia)
//   2. Confirm — súhrn + súvislosti + "Potvrdiť uloženie"
// Bez kliknutia "Potvrdiť uloženie" sa NIČ NEZAPÍŠE do DB.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import TelefonInput from '../components/TelefonInput.jsx'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth, fetchAuthJSON } from '../auth/fetchAuth.js'
import { useBreadcrumb } from '../components/Layout.jsx'
import { Btn, BtnAdd, BtnNav, BtnGhost, BtnDanger } from '../components/Btn.jsx'
import { Input, NumberInput, Textarea } from '../components/Input.jsx'
import AdresaBunky from '../components/AdresaBunky.jsx'   // PARTNER_ADRESA_V1: jeden blok adresy (0.J)
import { adresaSloz, adresaBunky, adresaZRegistra } from '../lib/adresa.js'
import { Modal } from '../components/Modal.jsx'
import HelpHint from '../components/HelpHint.jsx'
import { Table, THead, TBody, TR, TH, TD } from '../components/Table.jsx'
import { useToast } from '../components/Toast.jsx'
import SmsButton from '../components/SmsButton.jsx'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { usePositionMemory } from '../hooks/usePositionMemory.js'
import { useDirtyState } from '../hooks/useDirtyState.js'
import { useLock } from '../hooks/useLock.js'
import { COLORS, TYPO, SPACE, RADIUS, SK, hladaj } from '../theme.js'
import { DetailPane } from './PartnerDetail.jsx'
import RowMenu from '../components/RowMenu.jsx'
import ExportMenu from '../components/ExportMenu.jsx'
import { useConfirm } from '../components/ConfirmDialog.jsx'

const PARTNER_TYPES = [
  { key: 'all',       label: 'Všetci',     color: COLORS.muted, bg: 'transparent' },
  { key: 'odberatel', label: 'Odberateľ',  color: COLORS.nav,   bg: COLORS.navBg },
  { key: 'dodavatel', label: 'Dodávateľ',  color: COLORS.total, bg: COLORS.totalBg },
  { key: 'oboje',     label: 'Oboje',      color: '#7c3aed',    bg: 'rgba(124,58,237,0.10)' }
]

const typeBadge = (t) => {
  const cfg = PARTNER_TYPES.find(x => x.key === (t || 'odberatel')) || PARTNER_TYPES[1]
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      fontSize: TYPO.sizes.xs,
      fontWeight: TYPO.weights.semibold,
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.color}`,
      borderRadius: RADIUS.full,
      whiteSpace: 'nowrap'
    }}>{cfg.label}</span>
  )
}

export default function PartneriView() {
  const navigate = useNavigate()
  const { id: selectedId } = useParams()
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const { setTrail } = useBreadcrumb()

  // Data
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [oi, setOi] = useState([])
  const [ii, setIi] = useState([])

  // UI state
  const pos = usePositionMemory('partneri')
  const listRef = useRef(null)
  const [search, setSearch] = useState(pos.filter?.search || '')
  const [filterType, setFilterType] = useState(pos.filter?.type || 'all')
  const [newModal, setNewModal] = useState(false)
  const [editModal, setEditModal] = useState(null)   // null | partner object
  const [showKos, setShowKos] = useState(false)      // kos_v1 (28. 9. 2026): kôš namiesto archívu — 30 dní na obnovenie
  const [kosItems, setKosItems] = useState([])

  // Otvorí Edit modal pre vybraného partnera (2× klik, Enter, alebo "✎ Upraviť" tlačidlo)
  const openEdit = useCallback((partner) => {
    if (!partner) return
    setEditModal(partner)
  }, [])

  // Breadcrumb
  useEffect(() => {
    const selected = items.find(x => x.id === selectedId)
    setTrail([
      { label: 'Matrix v2', onClick: () => navigate('/') },
      { label: 'Partneri', onClick: () => navigate('/partneri') },
      ...(selected ? [{ label: selected.name }] : [])
    ])
    return () => setTrail([])
  }, [setTrail, navigate, items, selectedId])

  // Fetch partneri
  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchAuthJSON('/api/customers')
      setItems(Array.isArray(data) ? data : [])
    } catch (e) {
      toast.error(`Načítanie partnerov zlyhalo: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { reload() }, [reload])
  useLiveRefresh(reload)  // živé obnovenie (SSE, ústava 97)

  // kos_v1 — zoznam koša (samostatný stav, aby sa nemiešal s normálnym zoznamom partnerov)
  const loadKos = useCallback(async () => {
    try { const data = await fetchAuthJSON('/api/customers?kos=1'); setKosItems(Array.isArray(data) ? data : []) }
    catch (e) { toast.error(`Kôš sa nenačítal: ${e.message}`) }
  }, [toast])
  useEffect(() => { loadKos() }, [loadKos])
  const dokosa = async (c) => {
    const ok = await confirm({ title: 'Vymazať partnera', message: `Partner „${c.name}“ pôjde do koša. 30 dní sa dá obnoviť, potom sa definitívne zmaže.`, danger: true, confirmLabel: 'Vymazať' })
    if (!ok) return
    try {
      const r = await fetchAuth(`/api/customers/${c.id}`, { method: 'DELETE' })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(d.error || 'Nepodarilo sa dať partnera do koša.'); return }
      toast.success(`Partner „${c.name}“ je v koši.`)
      if (selectedId === c.id) navigate('/partneri')
      reload(); loadKos()
    } catch (e) { toast.error(e.message) }
  }
  const obnovit = async (k) => {
    try {
      const r = await fetchAuth(`/api/customers/${k.id}/obnovit`, { method: 'POST' })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(d.error || 'Obnovenie zlyhalo.'); return }
      toast.success(`Partner „${k.name}“ obnovený z koša.`)
      loadKos(); reload()
    } catch (e) { toast.error(e.message) }
  }

  // FA pre top3
  useEffect(() => {
    Promise.all([
      fetchAuth('/api/outgoing-invoices').then(r => r.ok ? r.json() : []).catch(() => []),
      fetchAuth('/api/incoming-invoices').then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([ois, iis]) => {
      setOi(Array.isArray(ois) ? ois : [])
      setIi(Array.isArray(iis) ? iis : [])
    })
  }, [])

  // Top 3 odberatelia — faktúry za stavebné práce (FVSVP)
  // 4. 10. 2026 (Mirko: „widget nemá zdroje na čerpanie“): filtrovalo sa invoice_number.startsWith('FVSP'),
  // lenže čísla faktúr sú číselné rady (1260…, 2260…, 3260…) — na „FVSP“ nezačína ani jedno, takže widget
  // nevrátil nikdy nič. Typ dokladu nie je v čísle, ale v stĺpci typ; MKsoft FVSP sa ukladá ako FVSVP
  // (MK_TYPY, server.js). Číslo nechávam ako druhú možnosť pre ručne vystavené faktúry (Kronika 1212).
  const topOdb = useMemo(() => {
    const map = {}
    oi.filter(i => (i.typ || '') === 'FVSVP' || (i.invoice_number || '').startsWith('FVSP')).forEach(i => {
      const k = i.customer_id || i.customer_name || '?'
      if (!map[k]) map[k] = { name: i.customer_name || k, total: 0, cnt: 0 }
      map[k].total += (i.amount_total || 0)
      map[k].cnt += 1
    })
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 3)
  }, [oi])

  // Top 3 dodavatelia
  const topDod = useMemo(() => {
    const custMap = {}
    items.forEach(c => { custMap[c.id] = c.name })
    const map = {}
    ii.filter(i => i.invoice_kind === 'nakladova' || i.doc_type === 'fa' || i.doc_type === 'blocok').forEach(i => {
      const k = i.partner_id || i.supplier_name || '?'
      const name = (i.partner_id && custMap[i.partner_id]) ? custMap[i.partner_id] : (i.supplier_name || i.partner_id || '?')
      if (!map[k]) map[k] = { name, total: 0, cnt: 0 }
      map[k].total += (i.amount_total || 0)
      map[k].cnt += 1
    })
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 3)
  }, [ii, items])

  // Filter
  const [pSortKey, setPSortKey] = useState('meno')
  const [pSortDir, setPSortDir] = useState(1)
  const setPSort = (k) => { if (k === pSortKey) setPSortDir(d => -d); else { setPSortKey(k); setPSortDir(1) } }
  const filtered = useMemo(() => {
    return items.filter(c => {
      if (filterType !== 'all' && (c.partner_type || 'odberatel') !== filterType) return false
      if (search.trim()) {
        const q = search.toLowerCase()
        return hladaj((c.name || ''), q)
          || hladaj((c.contact || ''), q)
          || (c.ico || '').includes(q)
      }
      return true
    }).sort((a, b) => {
      let r = 0
      if (pSortKey === 'ico') r = String(a.ico || '').localeCompare(String(b.ico || ''))
      else r = (a.name || '').localeCompare(b.name || '', 'sk')
      if (r === 0) r = (a.name || '').localeCompare(b.name || '', 'sk')
      return r * pSortDir
    })
  }, [items, search, filterType, pSortKey, pSortDir])

  // Šípky ↑↓ — navigujú medzi partnermi (zmena URL)
  // Klávesová navigácia zoznamu — zdieľaný hook (ústava 54.2, 56.5)
  useListKeyboard({
    items: filtered,
    selectedId,
    onSelect: (id) => navigate(`/partneri/${id}`),
    onOpen: (row) => openEdit(row),
    enabled: !newModal && !editModal,
  })
  // pamäť pozície — filter + hľadanie + scroll (ústava 53.3)
  useEffect(() => { pos.setFilter({ type: filterType, search }) }, [filterType, search, pos.setFilter])
  useEffect(() => {
    if (!loading && listRef.current) pos.restoreScroll(listRef.current)
  }, [loading])  // eslint-disable-line react-hooks/exhaustive-deps

  // Skratky
  useShortcuts(useMemo(() => ([
    { keys: 'alt+n',  label: 'Nový partner', scope: 'Partneri', handler: () => setNewModal(true) },
    { keys: '/',      label: 'Hľadať',       scope: 'Partneri', handler: () => {
      const el = document.querySelector('[data-partneri-search]')
      if (el) el.focus()
    }, preventDefault: true }
  ]), []))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, height: 'calc(100vh - 130px)' }}>

      {/* HLAVICKA + TOP3 — nad oboma stĺpcami */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.md, flexWrap: 'wrap', marginBottom: SPACE.md, background: COLORS.bg, borderTop: `4px solid ${COLORS.brand}`, borderRadius: '3px 3px 0 0', padding: `${SPACE.sm}px ${SPACE.md}px` }}>
          <h1 style={{ margin: 0, fontSize: TYPO.sizes.xxl, fontWeight: TYPO.weights.extra }}>Partneri</h1>
          <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.base }}>
            {loading ? 'Načítavam…' : `${items.length} celkom · ${filtered.length} zobrazených`}
          </span>
          <div style={{ flex: 1 }} />
          <BtnAdd onClick={() => setNewModal(true)} title="Alt+N">+ Nový partner</BtnAdd>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE.md }}>
          <TopCard title="Top 3 odberatelia (FVSVP)" items={topOdb} variant="nav" />
          <TopCard title="Top 3 dodávatelia" items={topDod} variant="total" />
        </div>
      </div>

      {/* MASTER-DETAIL: vľavo zoznam, vpravo detail */}
      <div style={{ display: 'flex', gap: SPACE.md, flex: 1, minHeight: 0 }}>

        {/* ── ĽAVÝ PANEL — zoznam ─────────────────────────────── */}
        <div style={{
          width: 300,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: COLORS.bg,
          border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.lg,
          overflow: 'hidden'
        }}>
          {/* Filter + search */}
          <div style={{ padding: SPACE.sm, borderBottom: `1px solid ${COLORS.line}`, display: 'flex', flexDirection: 'column', gap: SPACE.xs }}>
            <input
              data-partneri-search
              type="search"
              placeholder="Hľadať (stlač /)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: TYPO.sizes.sm,
                fontFamily: 'inherit',
                border: `1px solid ${COLORS.line}`,
                borderRadius: RADIUS.md
              }}
            />
            <div style={{ display: 'flex', gap: 2 }}>
              {PARTNER_TYPES.map(t => (
                <button
                  key={t.key}
                  onClick={() => setFilterType(t.key)}
                  style={{
                    flex: 1,
                    padding: '3px 6px',
                    fontSize: TYPO.sizes.xs,
                    fontWeight: TYPO.weights.semibold,
                    color: filterType === t.key ? '#fff' : t.color,
                    background: filterType === t.key ? t.color : 'transparent',
                    border: `1px solid ${t.color}`,
                    borderRadius: RADIUS.sm,
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >{t.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setShowKos(false)} style={{ flex: 1, padding: '3px 6px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: !showKos ? '#fff' : COLORS.text, background: !showKos ? COLORS.brand : 'transparent', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit' }}>Partneri</button>
              <button onClick={() => setShowKos(true)} style={{ flex: 1, padding: '3px 6px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: showKos ? '#fff' : COLORS.remove, background: showKos ? COLORS.remove : 'transparent', border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit' }}>Kôš{kosItems.length ? ` (${kosItems.length})` : ''}</button>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
              <span style={{ fontSize: 10, color: COLORS.muted, textTransform: 'uppercase', fontWeight: 700 }}>Zoradiť:</span>
              {[['meno', 'Meno'], ['ico', 'IČO']].map(([k, l]) => (
                <button key={k} onClick={() => setPSort(k)} style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${pSortKey === k ? COLORS.brand : COLORS.line}`, background: pSortKey === k ? COLORS.navBg : '#fff', color: pSortKey === k ? COLORS.brandD : COLORS.muted }}>{l} {pSortKey === k ? (pSortDir === 1 ? '↑' : '↓') : '↕'}</button>
              ))}
            </div>
          </div>

          {/* Zoznam */}
          <div
            ref={listRef}
            onScroll={e => pos.saveScroll(e.currentTarget.scrollLeft, e.currentTarget.scrollTop)}
            style={{ flex: 1, overflow: 'auto', outline: 'none', padding: 6 }}
          >
            {showKos && (
              kosItems.length === 0
                ? <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Kôš je prázdny.</div>
                : kosItems.map(k => (
                  <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: '8px 10px', marginBottom: 6, borderRadius: RADIUS.lg, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, opacity: 0.85 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: TYPO.weights.semibold, whiteSpace: 'normal', wordBreak: 'break-word' }}>{k.name}</div>
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>do koša {SK.date((k.kos_at || '').slice(0, 10))}{k.kos_by ? ' — ' + k.kos_by : ''}</div>
                    </div>
                    <Btn onClick={() => obnovit(k)}>Obnoviť</Btn>
                  </div>
                ))
            )}
            {!showKos && filtered.length === 0 && !loading && (
              <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                {items.length === 0 ? 'Žiadni partneri.' : 'Žiadne výsledky.'}
              </div>
            )}
            {!showKos && filtered.map(c => {
              const active = c.id === selectedId
              const stripCol = (PARTNER_TYPES.find(x => x.key === (c.partner_type || 'odberatel')) || PARTNER_TYPES[1]).color
              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/partneri/${c.id}`)}
                  onDoubleClick={() => openEdit(c)}
                  title="1× klik = detail · 2× klik = upraviť"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACE.sm,
                    padding: '8px 10px',
                    marginBottom: 6,
                    cursor: 'pointer',
                    background: COLORS.bg,
                    borderTop: `1px solid ${active ? COLORS.nav : COLORS.line}`,
                    borderRight: `1px solid ${active ? COLORS.nav : COLORS.line}`,
                    borderBottom: `1px solid ${active ? COLORS.nav : COLORS.line}`,
                    borderLeft: `4px solid ${stripCol}`,
                    borderRadius: RADIUS.lg,
                    boxShadow: active ? `0 0 0 1px ${COLORS.nav}` : '0 1px 2px rgba(0,0,0,0.05)',
                    fontSize: TYPO.sizes.sm,
                    userSelect: 'none'
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)' }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: active ? TYPO.weights.bold : TYPO.weights.semibold,
                      color: active ? COLORS.nav : COLORS.text,
                      whiteSpace: 'normal', wordBreak: 'break-word'
                    }}>{c.name}</div>
                    <div style={{
                      fontSize: TYPO.sizes.xs,
                      color: COLORS.muted,
                      fontFamily: TYPO.mono
                    }}>{c.id} · {c.ico || '—'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {typeBadge(c.partner_type)}
                    <RowMenu actions={[
                      { key: 'open',  label: 'Otvoriť detail',  icon: '↗',  onClick: () => navigate(`/partneri/${c.id}`) },
                      { key: 'edit',  label: 'Upraviť',         onClick: () => openEdit(c) },
                      { key: 'orsr',  label: 'Načítať z ORSR',  onClick: () => alert('ORSR refresh — pripravujeme') },
                      { key: 'merge', label: 'Zlúčiť s iným',   onClick: () => alert('Merge partnerov — pripravujeme') },
                      { key: 'print', label: 'Tlačiť kartu',    onClick: () => alert('Tlač — pripravujeme') },
                      { key: 'pdf',   label: 'Uložiť ako PDF',  onClick: () => alert('PDF — pripravujeme') },
                      { key: 'kos',   label: 'Vymazať', danger: true, onClick: () => dokosa(c) },
                    ]} />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Footer hint */}
          <div style={{
            padding: '4px 10px',
            borderTop: `1px solid ${COLORS.line}`,
            background: COLORS.bgSoft,
            fontSize: TYPO.sizes.xs,
            color: COLORS.muted,
            textAlign: 'center'
          }}>↑↓ select · Enter / 2× klik upraviť · / hľadať · Alt+N nový</div>
        </div>

        {/* ── PRAVÝ PANEL — detail ─────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          {selectedId ? (
            <DetailPane
              id={selectedId}
              onReload={reload}
              onEdit={(partner) => openEdit(partner)}
            />
          ) : (
            <EmptyPane />
          )}
        </div>
      </div>

      {/* NOVY PARTNER MODAL */}
      <NewPartnerModal
        open={newModal}
        onClose={() => setNewModal(false)}
        onCreated={(id) => {
          toast.success(`Partner vytvorený (${id})`)
          setNewModal(false)
          reload()
          navigate(`/partneri/${id}`)
        }}
      />

      {/* EDIT PARTNER MODAL (Task #7c — useLock + 2-fázový + Dirty + 409) */}
      <EditPartnerModal
        open={!!editModal}
        partner={editModal}
        onClose={() => setEditModal(null)}
        onSaved={(id) => {
          toast.success(`Partner ${id} uložený`)
          setEditModal(null)
          reload()
        }}
      />
    </div>
  )
}

// ============================================================
// TopCard
// ============================================================
function TopCard({ title, items, variant = 'nav' }) {
  const color = variant === 'total' ? COLORS.total : COLORS.nav
  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      overflow: 'hidden'
    }}>
      {/* HLAVIČKA WIDGETU — farebný pás + biele písmo (ústava 61.6) */}
      <div style={{
        background: color,
        color: '#fff',
        fontSize: TYPO.sizes.xs,
        fontWeight: TYPO.weights.bold,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '5px 12px'
      }}>{title}</div>
      <div style={{ padding: SPACE.md }}>
        {items.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Žiadne dáta</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((it, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'baseline', gap: SPACE.sm,
                padding: '4px 0',
                borderTop: i > 0 ? `1px solid ${COLORS.lineSoft}` : 'none',
                fontSize: TYPO.sizes.sm
              }}>
                <span style={{ fontWeight: TYPO.weights.bold, color, width: 18, textAlign: 'right' }}>{i + 1}.</span>
                <span style={{ flex: 1, fontWeight: TYPO.weights.semibold, whiteSpace: 'normal', wordBreak: 'break-word' }}>{it.name}</span>
                <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>{it.cnt}×</span>
                <span style={{ fontFamily: TYPO.mono, color, fontWeight: TYPO.weights.bold }}>{SK.eur(it.total, 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// EmptyPane — placeholder keď nikto nie je vybraný
// ============================================================
function EmptyPane() {
  return (
    <div style={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: COLORS.bg,
      border: `1px dashed ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      color: COLORS.muted,
      fontSize: TYPO.sizes.base,
      flexDirection: 'column',
      gap: SPACE.sm
    }}>
      
      <span>Vyber partnera v ľavom zozname</span>
      <span style={{ fontSize: TYPO.sizes.xs }}>(klik alebo šípky ↑↓)</span>
    </div>
  )
}

// PARTNER_ADRESA_V1: sídlo z registra (IČO / IČ DPH lookup vracia časti) rovno do buniek; keď register adresu nevráti, ostane vyplnená
const sidloZRegistra = (d, f) => { const a = adresaZRegistra(d); return (a.adresa_ulica || a.adresa_obec) ? { ...a, address: adresaSloz(a) } : { address: f.address } }

// ============================================================
// NewPartnerModal — 2 fázy: Formulár → Confirm (ÚSTAVA 12.0 BOD 3)
// ============================================================
function NewPartnerModal({ open, onClose, onCreated }) {
  const toast = useToast()
  const initial = useMemo(() => ({
    name: '', kind: 'firma', first_name: '', last_name: '', ico: '', dic: '', ic_dph: '', partner_type: 'odberatel',
    contact: '', phone: '', email: '', phone2: '', email2: '', phone_label: '', email_label: '', phone2_label: '', email2_label: '', address: '', iban: '', bank: '', notes: '',
    adresa_ulica: '', adresa_cislo: '', adresa_psc: '', adresa_obec: '', adresa_stat: 'SK'   // PARTNER_ADRESA_V1
  }), [])
  const form = useDirtyState(initial)
  const [phase, setPhase] = useState('form')         // 'form' | 'confirm'
  const [saving, setSaving] = useState(false)
  const [lookingUp, setLookingUp] = useState(false)
  const [dupWarn, setDupWarn] = useState(null)
  const [dirtyDialog, setDirtyDialog] = useState(false)
  // p32 (16. 9. 2026): IČO patrí našej subdodávke → zakladá sa v Zamestnancoch
  const [subWarn, setSubWarn] = useState(null)
  const icoCisla = String(form.value.ico || '').replace(/\D/g, '')
  useEffect(() => {
    setSubWarn(null)
    if (!open || icoCisla.length !== 8) return
    let zrus = false
    fetchAuth('/api/employees/check-ico/' + icoCisla).then(r => r.ok ? r.json() : null).then(j => { if (!zrus && j && j.employee && j.employee.employee_type === 'subdodavka') setSubWarn(j.employee) }).catch(() => {})
    return () => { zrus = true }
  }, [open, icoCisla])

  // Reset pri otvoreni
  useEffect(() => {
    if (open) {
      form.reset(initial)
      setDupWarn(null)
      setSaving(false)
      setPhase('form')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const updateField = (k, v) => form.setValue({ ...form.value, [k]: v })

  // ORSR lookup
  const lookupIco = async () => {
    const q = (form.value.ico || '').trim()
    if (!q) { toast.warn('Zadaj IČO alebo názov firmy'); return }
    setLookingUp(true)
    try {
      const compact = q.replace(/\s/g, '')
      const isIco = /^\d{8}$/.test(compact)
      let d = null
      if (isIco) {
        const r = await fetchAuth(`/api/ico/lookup?ico=${encodeURIComponent(compact)}`)
        const j = await r.json()
        if (!r.ok || j.error || j.ok === false) { toast.error(`ORSR: ${j.error || j.message || `HTTP ${r.status}`}`); return }
        d = j.data || j
      } else {
        const r = await fetchAuth(`/api/ico/lookup?name=${encodeURIComponent(q)}`)
        const j = await r.json()
        if (!r.ok || j.error) { toast.error(`ORSR: ${j.error || `HTTP ${r.status}`}`); return }
        const list = Array.isArray(j.data) ? j.data : (j.data ? [j.data] : [])
        if (!list.length) { toast.warn(`ORSR: nič sa nenašlo pre "${q}"`); return }
        let pick = list[0]
        if (list.length > 1) {
          const msg = `Našlo sa ${list.length} firiem:\n\n` + list.slice(0, 9).map((x, i) => `${i + 1}. ${x.name}${x.ico ? ` · IČO ${x.ico}` : ''}`).join('\n') + `\n\nZadaj číslo (1-${Math.min(list.length, 9)}):`
          const sel = window.prompt(msg, '1')
          const idx = parseInt(sel, 10) - 1
          if (isNaN(idx) || idx < 0 || idx >= list.length) { toast.warn('Výber zrušený'); return }
          pick = list[idx]
        }
        d = pick
        if (pick.orsr_path) { const r2 = await fetchAuth(`/api/ico/lookup?orsr_path=${encodeURIComponent(pick.orsr_path)}`); const j2 = await r2.json(); if (r2.ok && (j2.data || j2) && (j2.data || j2).name) d = j2.data || j2 }
        else if (pick.ico) { const r2 = await fetchAuth(`/api/ico/lookup?ico=${encodeURIComponent(pick.ico)}`); const j2 = await r2.json(); if (r2.ok && (j2.data || j2) && (j2.data || j2).name) d = j2.data || j2 }
      }
      if (!d || !d.name) { toast.warn('ORSR neviem identifikovať'); return }
      form.setValue({ ...form.value, name: d.name || form.value.name, ico: d.ico || form.value.ico, dic: d.dic || form.value.dic, ic_dph: d.ic_dph || form.value.ic_dph, ...sidloZRegistra(d, form.value) })
      toast.success(`ORSR: ${d.name}`)
    } catch (e) {
      toast.error(`Lookup zlyhal: ${e.message}`)
    } finally {
      setLookingUp(false)
    }
  }

  // VIES lookup
  const lookupIcDph = async () => {
    if (!form.value.ic_dph?.trim()) { toast.warn('Zadaj IČ DPH pred vyhľadaním'); return }
    setLookingUp(true)
    try {
      const r = await fetchAuth(`/api/ico/lookup?ic_dph=${encodeURIComponent(form.value.ic_dph.trim())}`)
      const j = await r.json()
      if (!r.ok || j.error || j.ok === false) {
        toast.error(`VIES: ${j.error || j.message || `HTTP ${r.status}`}`)
        return
      }
      const d = j.data || j
      if (!d || !d.name) { toast.warn('VIES neviem identifikovať'); return }
      form.setValue({
        ...form.value,
        name:    d.name    || form.value.name,
        ico:     d.ico     || form.value.ico,
        ic_dph:  d.ic_dph  || form.value.ic_dph,
        ...sidloZRegistra(d, form.value)
      })
      toast.success(`VIES: ${d.name}`)
    } catch (e) {
      toast.error(`VIES zlyhal: ${e.message}`)
    } finally {
      setLookingUp(false)
    }
  }

  // KROK 1: prejsť z formulára do confirm fázy
  const goToConfirm = () => {
    if (!form.value.name?.trim()) { toast.warn('Názov je povinný'); return }
    setDupWarn(null)
    setPhase('confirm')
  }

  // KROK 2: reálne uložiť — len po explicit potvrdení v confirm fáze
  const confirmSave = async () => {
    setSaving(true)
    setDupWarn(null)
    try {
      const r = await fetchAuth('/api/customers', {
        method: 'POST',
        body: JSON.stringify(form.value)
      })
      if (r.status === 409) {
        const j = await r.json()
        setDupWarn({ existingId: j.existingId, name: j.message, type: j.error })
        setPhase('form')    // vrátime do formulára aby Mirko videl dup warning
        setSaving(false)
        return
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.message || j.error || `HTTP ${r.status}`)
      }
      const d = await r.json()
      form.reset()
      onCreated && onCreated(d.id || d.lastInsertRowid || '?')
    } catch (e) {
      toast.error(`Uloženie zlyhalo: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (form.isDirty) setDirtyDialog(true)
    else onClose && onClose()
  }

  // FOOTER tlačidiel — líši sa podľa fázy
  const footer = phase === 'form' ? (
    <>
      <BtnGhost onClick={handleClose}>Zrušiť</BtnGhost>
      <BtnNav
        onClick={goToConfirm}
        disabled={!form.value.name?.trim()}
        title="Pokračovať na potvrdzovaciu obrazovku"
      >Pokračovať →</BtnNav>
    </>
  ) : (
    <>
      <BtnGhost onClick={() => setPhase('form')}>← Späť (upraviť)</BtnGhost>
      <BtnAdd onClick={confirmSave} loading={saving} disabled={saving}>
        {saving ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
      </BtnAdd>
    </>
  )

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={phase === 'form' ? 'Nový partner — krok 1: údaje' : 'Nový partner — krok 2: potvrdenie'}
        subtitle={phase === 'form' ? 'Vyplň údaje, potom Pokračuj na súhrn' : 'Skontroluj a potvrď — pred potvrdením sa NIČ neuloží'}
        isDirty={form.isDirty}
        onDirtyClose={() => setDirtyDialog(true)}
        footer={footer}
      >
        {phase === 'form' && (
          <FormPhase
            form={form}
            updateField={updateField}
            lookupIco={lookupIco}
            lookupIcDph={lookupIcDph}
            lookingUp={lookingUp}
            dupWarn={dupWarn}
            onClose={onClose}
            subWarn={subWarn}
          />
        )}
        {phase === 'confirm' && (
          <ConfirmPhase value={form.value} />
        )}
      </Modal>

      <Modal.DirtyConfirm
        open={dirtyDialog}
        onSave={() => { setDirtyDialog(false); goToConfirm() }}
        onContinue={() => setDirtyDialog(false)}
        onDiscard={() => { form.discard(); setDirtyDialog(false); onClose && onClose() }}
      />
    </>
  )
}

// ── Form phase (krok 1) ─────────────────────────────────
// p110 (22. 9. 2026, Mirko: „Lucia — partner, tri bodky, Upraviť nefunguje… robí to na všetkých partneroch"; T0017):
// formulár siahal na premennú subWarn, ktorá žije len v okne Nový partner → „ReferenceError: Can't find variable: subWarn"
// pri KAŽDOM otvorení (nový aj úprava). Upozornenie na subdodávku prichádza ako parameter; pri úprave je prázdne.
function FormPhase({ form, updateField, lookupIco, lookupIcDph, lookingUp, dupWarn, onClose, subWarn = null }) {
  // fyzická osoba vs firma — kind nie je DB stĺpec, pri edite ho odvodíme z priezviska (Mirko 22.7.)
  const kind = form.value.kind || (form.value.last_name ? 'fyzicka' : 'firma')
  // zafixuje kind='fyzicka' (v EDIT sa kind neseeduje → vymazanie priezviska by inak preplo späť na firmu počas písania)
  const setPerson = (fn, ln) => form.setValue({ ...form.value, kind: 'fyzicka', first_name: fn, last_name: ln, name: `${fn} ${ln}`.replace(/\s+/g, ' ').trim() })
  return (
    <>
      {/* TYP */}
      <div style={{ display: 'flex', gap: SPACE.sm, marginBottom: SPACE.md }}>
        {PARTNER_TYPES.filter(t => t.key !== 'all').map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => updateField('partner_type', t.key)}
            style={{
              padding: '6px 16px',
              fontSize: TYPO.sizes.sm,
              fontWeight: TYPO.weights.semibold,
              color: form.value.partner_type === t.key ? '#fff' : t.color,
              background: form.value.partner_type === t.key ? t.color : 'transparent',
              border: `1px solid ${t.color}`,
              borderRadius: RADIUS.md,
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Firma / Fyzická osoba (Mirko 22.7. — každý má meno a priezvisko) */}
      <div style={{ display: 'flex', gap: SPACE.sm, marginBottom: SPACE.md }}>
        {[['firma', 'Firma (s IČO)'], ['fyzicka', 'Fyzická osoba']].map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => {
            if (k === 'fyzicka') {
              let fn = form.value.first_name, ln = form.value.last_name
              if (!fn && !ln && (form.value.name || '').trim()) { const p = form.value.name.trim().split(/\s+/); fn = p[0] || ''; ln = p.slice(1).join(' ') }
              form.setValue({ ...form.value, kind: k, first_name: fn, last_name: ln, name: `${fn} ${ln}`.replace(/\s+/g, ' ').trim() })
            } else { updateField('kind', k) }
          }} style={{
            flex: 1, padding: '6px 16px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, fontFamily: 'inherit',
            border: `1px solid ${kind === k ? COLORS.nav : COLORS.line}`, borderRadius: RADIUS.md, cursor: 'pointer',
            background: kind === k ? COLORS.nav : 'transparent', color: kind === k ? '#fff' : COLORS.text,
          }}>{lbl}</button>
        ))}
      </div>

      {kind === 'firma' && (
        <>
          <div style={{ fontSize: TYPO.sizes.xs, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: SPACE.xs, display: 'flex', alignItems: 'center' }}>Identifikácia partnera <HelpHint section="7" text="ČO: zadaj IČO (8 číslic) alebo názov a klikni Hľadať ORSR — vyplní názov/adresu; IČ DPH over cez VIES. PREČO: jednotné údaje + systém kontroluje DUPLICITY (rovnaké IČO/názov) a upozorní. KAM: partner sa použije v CP, zákazkách aj faktúrach. Partneri sa NEMAŽÚ." /></div>
          <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'flex-end', marginBottom: SPACE.md }}>
            <Input label="IČO" value={form.value.ico} onChange={(v) => updateField('ico', v)} placeholder="napr. 36123456" hint="IČO (8 číslic) ALEBO názov firmy → Hľadať ORSR" style={{ flex: 1 }} />
            <Btn variant="nav" onClick={lookupIco} loading={lookingUp} disabled={lookingUp || !form.value.ico?.trim()}>Hľadať ORSR</Btn>
            <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, alignSelf: 'center' }}>vyplní aj bunky sídla</span>{/* PARTNER_ADRESA_V1: mockup 21. 10. */}
          </div>

          <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'flex-end', marginBottom: SPACE.md }}>
            <Input label="IČ DPH" value={form.value.ic_dph} onChange={(v) => updateField('ic_dph', v)} placeholder="napr. SK2020317456" style={{ flex: 1 }} />
            <Btn variant="nav" onClick={lookupIcDph} loading={lookingUp} disabled={lookingUp || !form.value.ic_dph?.trim()}>Hľadať VIES</Btn>
            <Input label="DIČ" value={form.value.dic} onChange={(v) => updateField('dic', v)} placeholder="napr. 2020317456" style={{ width: 180 }} />
          </div>
        </>
      )}

      {kind === 'fyzicka' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md, marginBottom: SPACE.md }}>
          <Input label="Meno *" value={form.value.first_name || ''} onChange={(v) => setPerson(v, form.value.last_name || '')} placeholder="napr. Ján" />
          <Input label="Priezvisko *" value={form.value.last_name || ''} onChange={(v) => setPerson(form.value.first_name || '', v)} placeholder="napr. Novák" />
        </div>
      ) : (
        <Input label="Názov *" value={form.value.name} onChange={(v) => updateField('name', v)} placeholder="napr. STAVMAT s.r.o." required fullWidth style={{ marginBottom: SPACE.md }} />
      )}

      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: SPACE.md, lineHeight: 1.5 }}>
        Firma alebo SZČO, ktorá pre nás <b>robí práce na zákazkách</b> (subdodávka, napr. potery), sa zakladá v <b>Zamestnancoch › Nový zamestnanec › Typ Subdodávka</b> — tam je v Rozpise prác a partner sa tu vytvorí sám.
      </div>
      {subWarn && (
        <div style={{ background: 'rgba(245,158,11,0.08)', border: `1px solid ${COLORS.total}`, borderRadius: RADIUS.md, padding: SPACE.md, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm }}>
          <div style={{ color: COLORS.total, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>Toto IČO patrí našej subdodávke — {subWarn.name || subWarn.id}</div>
          <div style={{ color: COLORS.text }}>Subdodávka sa upravuje v Zamestnancoch; partner k nej už existuje alebo sa vytvorí sám. Nového partnera nezakladaj.</div>
        </div>
      )}
      {dupWarn && (
        <div style={{
          background: COLORS.removeBg, border: `1px solid ${COLORS.remove}`,
          borderRadius: RADIUS.md, padding: SPACE.md, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm
        }}>
          <div style={{ color: COLORS.remove, fontWeight: TYPO.weights.semibold, marginBottom: 4 }}>
            Duplicita: {dupWarn.type === 'DUPLICATE_ICO' ? 'rovnaké IČO' : 'rovnaký názov'}
          </div>
          <div style={{ color: COLORS.text }}>{dupWarn.name}</div>
        </div>
      )}

      {/* osoba_v1 (8. 10. 2026, ústava 139): človek je titul + meno + priezvisko — nikdy jedno pole.
          Celé meno sa z nich dopočíta na serveri, takže doklady a zoznamy fungujú ďalej. */}
      <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr 1fr', gap: SPACE.md, marginBottom: SPACE.md }}>
        <Input label="Titul" value={form.value.contact_titul || ''} onChange={(v) => updateField('contact_titul', v)} placeholder="Ing." />
        <Input label="Kontaktná osoba — meno" value={form.value.contact_meno || ''} onChange={(v) => updateField('contact_meno', v)} placeholder="Juraj" />
        <Input label="Priezvisko" value={form.value.contact_priezvisko || ''} onChange={(v) => updateField('contact_priezvisko', v)} placeholder="Šulík" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: SPACE.md, marginBottom: SPACE.md }}>
        <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Telefón</div><TelefonInput value={form.value.phone} onChange={(v) => updateField('phone', v)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d6dde6', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' }} /></div>
        <Input label="Email" value={form.value.email} onChange={(v) => updateField('email', v)} placeholder="firma@example.sk" type="email" />
      </div>
      {/* Druhý kontakt — Mirko 24.7.: flexibilita (fakturačný, technik, mobil na stavbu…) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: SPACE.md, marginBottom: SPACE.md }}>
        <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Telefón 2 (voliteľné)</div><TelefonInput value={form.value.phone2 || ''} onChange={(v) => updateField('phone2', v)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d6dde6', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' }} /></div>
        <Input label="Popis tel. 2" value={form.value.phone2_label || ''} onChange={(v) => updateField('phone2_label', v)} placeholder="napr. mobil na stavbu" />
        <Input label="Email 2 (voliteľné)" value={form.value.email2 || ''} onChange={(v) => updateField('email2', v)} placeholder="druhy@example.sk" type="email" />
        <Input label="Popis emailu 2" value={form.value.email2_label || ''} onChange={(v) => updateField('email2_label', v)} placeholder="napr. fakturačný" />
      </div>

      {/* PARTNER_ADRESA_V1 (21. 10. 2026): adresa v bunkách ako karta zamestnanca a zákazka — jeden riadok (address) sa z nich poskladá pre CP, FA a doklady */}
      <AdresaBunky hodnota={form.value} nadpis={kind === 'fyzicka' ? 'Adresa' : 'Sídlo'} zmen={(o) => form.setValue({ ...form.value, ...o, address: adresaSloz(o) })} style={{ marginBottom: SPACE.md }}
        riadok={form.value.address || ''} poznamka="Pri fyzickej osobe sa blok volá „Adresa“. V obci bez ulíc: Ulica = názov obce." />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: SPACE.md, marginBottom: SPACE.md }}>
        <Input label="IBAN" value={form.value.iban} onChange={(v) => updateField('iban', v)} placeholder="SK..." />
        <Input label="Banka" value={form.value.bank} onChange={(v) => updateField('bank', v)} placeholder="názov banky" />
      </div>

      <Textarea label="Poznámka" value={form.value.notes} onChange={(v) => updateField('notes', v)} placeholder="vlastné poznámky" rows={2} />
    </>
  )
}

// ── Confirm phase (krok 2 — ÚSTAVA 12.0 bod 3) ───────────
// mode='new'  → text "Nový partner vznikne ..."
// mode='edit' → text "Zmeny sa uložia ..." + DIFF (žlté zvýraznenie zmenených polí)
function ConfirmPhase({ value, mode = 'new', original = null }) {
  const isEdit = mode === 'edit' && original
  const typeLabel = (v) => v === 'dodavatel' ? 'Dodávateľ' : v === 'oboje' ? 'Oboje' : 'Odberateľ'

  // Pre konzistenciu — pripravíme aj `original` zhrnutie (pre DIFF porovnanie)
  const fmt = (v) => (v === null || v === undefined || v === '') ? '—' : String(v)

  // Definícia polí na zobrazenie
  const FIELDS = [
    { k: 'Typ',             prop: 'partner_type', transform: typeLabel },
    { k: 'Názov',           prop: 'name',          bold: true },
    { k: 'IČO',             prop: 'ico',           mono: true },
    { k: 'DIČ',             prop: 'dic',           mono: true },
    { k: 'IČ DPH',          prop: 'ic_dph',        mono: true },
    { k: 'Kontaktná osoba', prop: 'contact' },
    { k: 'Telefón',         prop: 'phone' },
    { k: 'Email',           prop: 'email' },
    { k: 'Adresa',           prop: 'address' },
    { k: 'IBAN',            prop: 'iban',          mono: true },
    { k: 'Banka',           prop: 'bank' },
    { k: 'Poznámka',        prop: 'notes' }
  ]

  // Spočítaj koľko polí sa zmenilo (len v edit móde)
  const changedCount = isEdit
    ? FIELDS.filter(f => (original[f.prop] ?? '') !== (value[f.prop] ?? '')).length
    : 0

  return (
    <>
      {/* BANNER — rozdielne pre new vs edit */}
      <div style={{
        padding: SPACE.md,
        background: COLORS.addBg,
        border: `1px solid ${COLORS.add}`,
        borderRadius: RADIUS.md,
        marginBottom: SPACE.lg,
        color: COLORS.text,
        fontSize: TYPO.sizes.sm
      }}>
        <div style={{ fontWeight: TYPO.weights.bold, marginBottom: 4, color: COLORS.add }}>Pred potvrdením skontroluj:</div>
        {isEdit ? (
          <>
            <strong>Zmeny sa uložia</strong> k existujúcemu partnerovi <strong>{original.name}</strong> (<code style={{ fontFamily: TYPO.mono }}>{original.id}</code>).
            {' '}<strong>Žiadne dáta sa zatiaľ nezmenili</strong> — uloženie nastane až po stlačení „✓ Potvrdiť uloženie".
            {changedCount > 0 && (
              <div style={{ marginTop: 6, color: COLORS.warn, fontWeight: TYPO.weights.semibold }}>
                Zmenené polia: <strong>{changedCount}</strong> (žlto zvýraznené v tabuľke nižšie)
              </div>
            )}
            {changedCount === 0 && (
              <div style={{ marginTop: 6, color: COLORS.muted }}>
                Žiadne polia neboli zmenené — môžeš ísť späť a upraviť, alebo zavrieť.
              </div>
            )}
          </>
        ) : (
          <>
            <strong>Nový partner vznikne</strong> s nasledujúcimi údajmi. <strong>Žiadne dáta sa zatiaľ neuložili</strong> — uloženie nastane až po stlačení „✓ Potvrdiť uloženie".
          </>
        )}
      </div>

      {/* TABUĽKA polí — v edit móde 3 stĺpce (pole | pôvodne | nová) s DIFF, v new móde 2 stĺpce */}
      <div style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.lg,
        padding: SPACE.lg,
        marginBottom: SPACE.lg
      }}>
        {isEdit ? (
          <>
            {/* Hlavička DIFF tabuľky */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr 1fr',
              gap: 4,
              paddingBottom: SPACE.xs,
              marginBottom: SPACE.xs,
              borderBottom: `2px solid ${COLORS.line}`,
              fontSize: TYPO.sizes.xs,
              fontWeight: TYPO.weights.semibold,
              color: COLORS.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}>
              <div>Pole</div>
              <div>Pôvodne</div>
              <div>Nová hodnota</div>
            </div>
            {/* Riadky DIFF */}
            {FIELDS.map((f, i) => {
              const oldRaw = original[f.prop] ?? ''
              const newRaw = value[f.prop] ?? ''
              const changed = oldRaw !== newRaw
              const oldDisplay = f.transform ? f.transform(oldRaw) : fmt(oldRaw)
              const newDisplay = f.transform ? f.transform(newRaw) : fmt(newRaw)
              return (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: '160px 1fr 1fr',
                  gap: 4,
                  padding: '4px 0',
                  borderBottom: i < FIELDS.length - 1 ? `1px solid ${COLORS.lineSoft}` : 'none',
                  background: changed ? 'rgba(245,158,11,0.10)' : 'transparent',  // žlté zvýraznenie ZMENENÉHO riadka
                  borderRadius: changed ? RADIUS.sm : 0,
                  paddingLeft: changed ? 6 : 0,
                  marginLeft: changed ? -6 : 0,
                  paddingRight: changed ? 6 : 0,
                  marginRight: changed ? -6 : 0
                }}>
                  <div style={{
                    color: COLORS.muted,
                    fontSize: TYPO.sizes.sm,
                    fontWeight: changed ? TYPO.weights.semibold : TYPO.weights.normal
                  }}>
                    {changed && <span style={{ color: COLORS.warn, marginRight: 4 }}></span>}
                    {f.k}
                  </div>
                  <div style={{
                    color: changed ? COLORS.muted : COLORS.text,
                    fontFamily: f.mono ? TYPO.mono : 'inherit',
                    fontSize: TYPO.sizes.sm,
                    textDecoration: changed ? 'line-through' : 'none',
                    whiteSpace: 'pre-wrap'
                  }}>{oldDisplay}</div>
                  <div style={{
                    color: COLORS.text,
                    fontFamily: f.mono ? TYPO.mono : 'inherit',
                    fontSize: TYPO.sizes.sm,
                    fontWeight: changed ? TYPO.weights.bold : (f.bold ? TYPO.weights.semibold : TYPO.weights.normal),
                    whiteSpace: 'pre-wrap'
                  }}>
                    {changed && <span style={{ color: COLORS.warn, marginRight: 4 }}>→</span>}
                    {newDisplay}
                  </div>
                </div>
              )
            })}
          </>
        ) : (
          /* NEW mode — pôvodné 2 stĺpce */
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 4 }}>
            {FIELDS.map((f, i) => {
              const v = f.transform ? f.transform(value[f.prop]) : fmt(value[f.prop])
              return (
                <React.Fragment key={i}>
                  <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: '4px 0' }}>{f.k}</div>
                  <div style={{
                    color: COLORS.text,
                    fontFamily: f.mono ? TYPO.mono : 'inherit',
                    fontWeight: f.bold ? TYPO.weights.semibold : TYPO.weights.normal,
                    padding: '4px 0',
                    borderBottom: i < FIELDS.length - 1 ? `1px solid ${COLORS.lineSoft}` : 'none',
                    whiteSpace: 'pre-wrap'
                  }}>{v}</div>
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>

      {/* SÚVISLOSTI — rozdielne pre new vs edit */}
      <div style={{
        padding: SPACE.md,
        background: COLORS.navBg,
        border: `1px solid ${COLORS.nav}`,
        borderRadius: RADIUS.md,
        fontSize: TYPO.sizes.sm,
        color: COLORS.text
      }}>
        <div style={{ fontWeight: TYPO.weights.bold, marginBottom: 6, color: COLORS.nav }}>
          {isEdit ? 'Súvislosti — kde sa zmena prejaví:' : 'Súvislosti — kde sa partner zobrazí:'}
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: TYPO.line.relaxed }}>
          {isEdit ? (
            <>
              <li><strong>Všetky existujúce zákazky</strong> s týmto partnerom (objednávateľ aktualizovaný)</li>
              <li><strong>Všetky existujúce FA</strong> (FVSP/FVO/FVE/Došlé) — kontaktné údaje sa updatnú spätne</li>
              <li><strong>Cenové ponuky</strong> rozpracované — adresát aktualizovaný</li>
              {(value.partner_type !== original.partner_type) && (
                <li style={{ color: COLORS.warn, fontWeight: TYPO.weights.semibold }}>
                  Zmena typu ({typeLabel(original.partner_type)} → {typeLabel(value.partner_type)}) ovplyvní filter „Odberatelia/Dodávatelia"
                </li>
              )}
              {value.ico !== original.ico && (
                <li style={{ color: COLORS.warn, fontWeight: TYPO.weights.semibold }}>
                  Zmena IČO — MKsoft pairing sa môže rozsynchronizovať (spárovanie podľa firmaid)
                </li>
              )}
              <li style={{ color: COLORS.muted }}>Štatistiky (Top 3 / Trinity / RD vs Firma) sa prepočítajú automaticky</li>
            </>
          ) : (
            <>
              {(value.partner_type === 'odberatel' || value.partner_type === 'oboje') && (
                <>
                  <li>Zákazky — výber objednávateľa pri novej zákazke</li>
                  <li>Cenové ponuky — možný adresát CP</li>
                  <li>Vystavené FA (FVSP/FVO) — partner v hlavičke FA</li>
                </>
              )}
              {(value.partner_type === 'dodavatel' || value.partner_type === 'oboje') && (
                <>
                  <li>Došlé FA — dodávateľ pri zápise došlej faktúry</li>
                  <li>Bločky — výber dodávateľa pri AI skene bločka</li>
                  <li>VSZAK/VSOST — pri MKsoft sync ako zdroj materiálu</li>
                </>
              )}
              {value.ico && (
                <li>MKsoft pairing — pri zhode IČO sa partner automaticky spojí s firmou v MKsoft</li>
              )}
              <li style={{ color: COLORS.muted }}>Štatistiky — bude započítaný v Top 3 / Trinity / RD vs Firma</li>
            </>
          )}
        </ul>
      </div>

      <div style={{
        marginTop: SPACE.md,
        padding: SPACE.sm,
        background: COLORS.bgSoft,
        borderRadius: RADIUS.md,
        fontSize: TYPO.sizes.xs,
        color: COLORS.muted,
        textAlign: 'center'
      }}>
        Ústava sek. 12.0 bod 3 — žiadny zápis bez explicitného potvrdenia · partner sa NEMAŽE (soft archív, sek. bod 1)
      </div>
    </>
  )
}

// ============================================================
// EditPartnerModal — edit existujúceho partnera (Task #7c)
// Sek. 12.0 bod 3 — 2-fázový Form→Confirm pred PUT
// Sek. 53.1 — Dirty dialog pri zatváraní
// Sek. 53.7 — useLock multi-user (ak iný edituje → read-only mode)
// Sek. 13.3/4 — 409 DUPLICATE guard
// ============================================================
function EditPartnerModal({ open, partner, onClose, onSaved }) {
  const toast = useToast()
  const { user } = useAuth()
  const initial = useMemo(() => partner ? { ...partner, ...adresaBunky(partner, 'address') } : null, [partner])   // PARTNER_ADRESA_V1: bunky, prázdne rozložené z riadku
  const form = useDirtyState(initial || {})

  const [phase, setPhase] = useState('form')   // 'form' | 'confirm'
  const [saving, setSaving] = useState(false)
  const [lookingUp, setLookingUp] = useState(false)
  const [dupWarn, setDupWarn] = useState(null)
  const [dirtyDialog, setDirtyDialog] = useState(false)

  // useLock (sek. 53.7) — len ak je modal otvorený A partner existuje
  const { lockInfo, acquired, release } = useLock(
    open && partner ? 'customer' : null,
    open && partner ? partner.id : null,
    user
  )

  // Reset pri otvorení / zmene partnera
  useEffect(() => {
    if (open && partner) {
      form.reset({ ...partner, ...adresaBunky(partner, 'address') })   // PARTNER_ADRESA_V1
      setPhase('form')
      setDupWarn(null)
      setSaving(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partner?.id])

  const updateField = (k, v) => form.setValue({ ...form.value, [k]: v })

  // Lookup helpers — rovnaké ako v NewPartnerModal
  const lookupIco = async () => {
    if (!form.value.ico?.trim()) { toast.warn('Zadaj IČO pred vyhľadaním'); return }
    setLookingUp(true)
    try {
      const r = await fetchAuth(`/api/ico/lookup?ico=${encodeURIComponent(form.value.ico.trim())}`)
      const j = await r.json()
      if (!r.ok || j.error || j.ok === false) { toast.error(`ORSR: ${j.error || j.message || `HTTP ${r.status}`}`); return }
      const d = j.data || j
      if (!d || !d.name) { toast.warn('ORSR neviem identifikovať'); return }
      form.setValue({
        ...form.value,
        name:    d.name    || form.value.name,
        ico:     d.ico     || form.value.ico,
        dic:     d.dic     || form.value.dic,
        ic_dph:  d.ic_dph  || form.value.ic_dph,
        ...sidloZRegistra(d, form.value)
      })
      toast.success(`ORSR: ${d.name}`)
    } catch (e) { toast.error(`Lookup zlyhal: ${e.message}`) }
    finally { setLookingUp(false) }
  }

  const lookupIcDph = async () => {
    if (!form.value.ic_dph?.trim()) { toast.warn('Zadaj IČ DPH pred vyhľadaním'); return }
    setLookingUp(true)
    try {
      const r = await fetchAuth(`/api/ico/lookup?ic_dph=${encodeURIComponent(form.value.ic_dph.trim())}`)
      const j = await r.json()
      if (!r.ok || j.error || j.ok === false) { toast.error(`VIES: ${j.error || j.message || `HTTP ${r.status}`}`); return }
      const d = j.data || j
      if (!d || !d.name) { toast.warn('VIES neviem identifikovať'); return }
      form.setValue({
        ...form.value,
        name: d.name || form.value.name, ico: d.ico || form.value.ico,
        ic_dph: d.ic_dph || form.value.ic_dph, ...sidloZRegistra(d, form.value)
      })
      toast.success(`VIES: ${d.name}`)
    } catch (e) { toast.error(`VIES zlyhal: ${e.message}`) }
    finally { setLookingUp(false) }
  }

  const goToConfirm = () => {
    if (!form.value.name?.trim()) { toast.warn('Názov je povinný'); return }
    setDupWarn(null)
    setPhase('confirm')
  }

  // KROK 2: explicit potvrdenie → PUT
  const confirmSave = async () => {
    if (!partner?.id) return
    setSaving(true)
    setDupWarn(null)
    try {
      // p53 (18. 9. 2026, Lucia: „na Partneroch nie je možné vykonávať úpravu existujúceho partnera — vyhadzuje chybovú hlášku“):
      // karta má často starú časovú pečiatku, lebo partnerov medzitým prepíše hodinový sync z MKsoftu. Server to bral ako zmenu
      // od iného používateľa a uloženie odmietol. Preto si pred uložením vyzdvihneme čerstvý záznam a pošleme jeho pečiatku.
      let cerstvy = null
      try { const g = await fetchAuth(`/api/customers/${partner.id}`); if (g.ok) cerstvy = await g.json() } catch (_) {}
      const telo = Object.assign({}, form.value, cerstvy && cerstvy.updated_at ? { updated_at: cerstvy.updated_at } : {})
      const r = await fetchAuth(`/api/customers/${partner.id}`, {
        method: 'PUT',
        body: JSON.stringify(telo)
      })
      if (r.status === 409) {
        const j = await r.json().catch(() => ({}))
        if (String(j.error || '') === 'CONFLICT') {
          toast.error('Kartu partnera medzitým zmenil niekto iný. Zavri ju, otvor znova a zmenu zopakuj — nech sa jeho úprava nestratí.')
          setPhase('form')
          setSaving(false)
          return
        }
        setDupWarn({ existingId: j.existingId, name: j.message, type: j.error })
        setPhase('form')
        setSaving(false)
        return
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.message || j.error || `HTTP ${r.status}`)
      }
      form.reset(form.value)
      release()
      onSaved && onSaved(partner.id)
    } catch (e) {
      toast.error(`Uloženie zlyhalo: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (form.isDirty) setDirtyDialog(true)
    else { release(); onClose && onClose() }
  }

  // Read-only mode ak je lock zaberané iným
  const isLocked = !acquired && !!lockInfo?.by

  // Footer
  const footer = isLocked ? (
    <BtnGhost onClick={handleClose}>Zavrieť</BtnGhost>
  ) : phase === 'form' ? (
    <>
      <BtnGhost onClick={handleClose}>Zrušiť</BtnGhost>
      <BtnNav onClick={goToConfirm} disabled={!form.value.name?.trim() || !form.isDirty}
        title={!form.isDirty ? 'Žiadne zmeny — nič na uloženie' : 'Pokračovať na potvrdzovaciu obrazovku'}>
        Pokračovať →
      </BtnNav>
    </>
  ) : (
    <>
      <BtnGhost onClick={() => setPhase('form')}>← Späť (upraviť)</BtnGhost>
      <BtnAdd onClick={confirmSave} loading={saving} disabled={saving}>
        {saving ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
      </BtnAdd>
    </>
  )

  if (!open || !partner) return null

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={isLocked ? `Editácia zamknutá — ${partner.name}` : phase === 'form' ? `Upraviť: ${partner.name}` : `Potvrdiť zmeny — ${partner.name}`}
        subtitle={isLocked
          ? 'Niekto iný práve edituje tohto partnera'
          : phase === 'form'
            ? `ID: ${partner.id} · uprav polia, potom Pokračuj na súhrn`
            : 'Skontroluj zmeny — pred potvrdením sa NIČ neuloží'}
        isDirty={form.isDirty}
        onDirtyClose={() => setDirtyDialog(true)}
        footer={footer}
      >
        {isLocked ? (
          <LockedView lockedBy={lockInfo.by} partner={partner} />
        ) : phase === 'form' ? (
          <FormPhase
            form={form} updateField={updateField}
            lookupIco={lookupIco} lookupIcDph={lookupIcDph}
            lookingUp={lookingUp} dupWarn={dupWarn} onClose={onClose}
            mode="edit"
            originalId={partner.id}
          />
        ) : (
          <ConfirmPhase value={form.value} mode="edit" original={partner} />
        )}
      </Modal>

      <Modal.DirtyConfirm
        open={dirtyDialog}
        onSave={() => { setDirtyDialog(false); goToConfirm() }}
        onContinue={() => setDirtyDialog(false)}
        onDiscard={() => { form.discard(); setDirtyDialog(false); release(); onClose && onClose() }}
      />
    </>
  )
}

// ── LockedView — zobrazenie ak edituje niekto iný (sek. 53.7) ────
function LockedView({ lockedBy, partner }) {
  return (
    <div>
      <div style={{
        padding: SPACE.lg,
        background: COLORS.removeBg,
        border: `1px solid ${COLORS.remove}`,
        borderRadius: RADIUS.md,
        marginBottom: SPACE.lg,
        fontSize: TYPO.sizes.base,
        textAlign: 'center'
      }}>
        
        <div style={{ fontWeight: TYPO.weights.bold, color: COLORS.remove, marginBottom: SPACE.xs }}>
          Edituje: {lockedBy}
        </div>
        <div style={{ color: COLORS.text, fontSize: TYPO.sizes.sm }}>
          Tohto partnera práve upravuje iný používateľ. Počkaj, kým skončí, alebo si žiadaj o uvoľnenie.
        </div>
        <div style={{ marginTop: SPACE.md, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          Lock sa automaticky uvoľní po zavretí editora alebo po 60 sekundách nečinnosti.
        </div>
      </div>

      {/* Read-only zobrazenie údajov */}
      <div style={{ opacity: 0.7 }}>
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: SPACE.sm, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Aktuálne údaje (read-only)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 4, fontSize: TYPO.sizes.sm }}>
          {[
            ['Typ', partner.partner_type === 'dodavatel' ? 'Dodávateľ' : partner.partner_type === 'oboje' ? 'Oboje' : 'Odberateľ'],
            ['Názov', partner.name],
            ['IČO', partner.ico || '—'],
            ['IČ DPH', partner.ic_dph || '—'],
            ['Email', partner.email || '—'],
            ...(partner.email2 ? [['Email 2' + (partner.email2_label ? ' (' + partner.email2_label + ')' : ''), partner.email2]] : []),
            ...(partner.phone2 ? [['Telefón 2' + (partner.phone2_label ? ' (' + partner.phone2_label + ')' : ''), partner.phone2]] : []),
            ['Telefón', partner.phone ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{partner.phone} <SmsButton to={partner.phone} label="SMS" style={{ padding: '3px 11px', fontSize: 12, fontWeight: 700, border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', borderRadius: 8 }} /></span> : '—'],
            ...(partner.linked_employee_id ? [['Aj zamestnanec',
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: COLORS.total, fontWeight: TYPO.weights.semibold }}>
                <span style={{ fontFamily: TYPO.mono }}>{partner.linked_employee_id}</span>
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontStyle: 'italic', fontWeight: 'normal' }}>
                  · SZČO partner ↔ zamestnanec (ústava 6+11.6) · pozor pri ukončení spolupráce
                </span>
              </span>
            ]] : [])
          ].map(([k, v]) => (
            <React.Fragment key={k}>
              <div style={{ color: COLORS.muted }}>{k}</div>
              <div>{v}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
